import React, { useContext, useMemo, useRef } from 'react';

import PropTypes from 'prop-types';
import {
  Box, Container, Grid,
  Typography,
} from '@material-ui/core';
import clsx from 'clsx';

import { Button } from '../../../../components';

import {
  CALC_MODE_PRECIO_INMUEBLE,
  MINIMUM_CREDIT_VALUE,
  PERFILADOR_VARIANTE_LANDING,
} from '../../../../constants';
import { PerfiladorInput } from '../PerfiladorInput';
import { FooterTextPerfilador } from '../FooterTextPerfilador';
import { TabsContext } from '../../../../context/tabs/tabsContext';
import { usePerfilador } from '../../../../hooks/usePerfilador';

import useStyles from './useStyles';
import { getValueMinimumErrorMessage, isGreaterThanOrEqualThanValue } from '../../../../utils/simulator.utils';
import { reportFormCustomValidityByFieldName } from '../../../../utils';

const FIELD_PROPIETY_NAME = 'propietyField';

export const PerfiladorUniqueScreen = ({
  onSubmit,
  showFooter,
  footerText,
  buttonText,
}) => {
  const formRef = useRef();
  const classes = useStyles();
  const {
    inputValue: propietyValue,
    inputNumber: valuePropNumber,
    engancheNumber = 0,
    plazoNumber,
    pagoMensual = 0,
    montoCredito = 0,
    percentageEnganche = 0,
    montoGastosEscritura = 0,
    onChange,
  } = usePerfilador({
    calcMode: CALC_MODE_PRECIO_INMUEBLE,
    useDesembolsoMode: true,
  });

  const { tabsExtraClasses, tabsVariant } = useContext(TabsContext);

  const inputData = {
    label: '',
    value: '',
    name: FIELD_PROPIETY_NAME,
    placeHolder: '$1,000,000',
    required: true,
    type: 'precio',
    disabled: false,
    hideLabel: true,
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const { isValid } = isGreaterThanOrEqualThanValue(valuePropNumber, MINIMUM_CREDIT_VALUE);
    if (!isValid) {
      return reportFormCustomValidityByFieldName(
        formRef.current,
        FIELD_PROPIETY_NAME,
        getValueMinimumErrorMessage(),
      );
    }
    const storageData = {
      importe: montoCredito,
      meses: plazoNumber * 12,
      aforo: (montoCredito / valuePropNumber).toFixed(4),
      enganche: engancheNumber,
      year: plazoNumber,
      plazo: `${plazoNumber} años`,
      mensualidad: pagoMensual,
      propietyValue: valuePropNumber,
      propietyValueCurrency: propietyValue,
      montoGastosEscritura,
      percentageEnganche,
    };
    return onSubmit({
      storageData,
      event,
    });
  };

  const inputLabel = useMemo(() => {
    switch (tabsVariant) {
      case PERFILADOR_VARIANTE_LANDING:
      default:
        return '¿Cuál es el valor de tu propiedad?:';
    }
  }, [tabsVariant]);

  return (
    <Container
      component="form"
      onSubmit={handleSubmit}
      ref={formRef}
      disableGutters
      autoComplete="off"
    >
      <Grid
        container
        spacing={2}
        className={clsx(classes.container, {
          [tabsExtraClasses?.gridContainer]: tabsExtraClasses?.gridContainer,
        })}
      >
        <Grid
          item
          xs={12}
          md={6}
          className={clsx(classes.grid, {
            [tabsExtraClasses?.gridLeft]: tabsExtraClasses?.gridLeft,
          })}
        >
          <PerfiladorInput
            titleLabel={inputLabel}
            disabled={inputData.disabled}
            hideLabel={inputData.hideLabel}
            label={inputData.label}
            placeHolder={inputData.placeHolder}
            required={inputData.required}
            type={inputData.type}
            value={propietyValue}
            onChange={onChange}
            isEmpty={valuePropNumber <= 0}
            name={FIELD_PROPIETY_NAME}
            classes={{
              input: clsx(classes.input, {
                [tabsExtraClasses?.input]: tabsExtraClasses.input,
              }),
              title: clsx(classes.title, {
                [tabsExtraClasses?.title]: tabsExtraClasses.title,
              }),
            }}
            validations={[
              (inputValue) => isGreaterThanOrEqualThanValue(
                inputValue,
                MINIMUM_CREDIT_VALUE,
                getValueMinimumErrorMessage(),
              ),
            ]}
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={6}
          className={classes?.footerTextContainer}
        >
          {
            footerText && (
              <Typography className={classes?.footerText}>{footerText}</Typography>
            )
          }
        </Grid>
      </Grid>

      <Box className={classes.btnContainer}>
        <Button
          className={clsx(classes.btn, {
            [tabsExtraClasses?.btn]: tabsExtraClasses.btn,
          })}
          type="submit"
        >
          {buttonText}
        </Button>
      </Box>
      {showFooter && <FooterTextPerfilador />}
    </Container>
  );
};

PerfiladorUniqueScreen.propTypes = {
  onSubmit: PropTypes.func,
  showFooter: PropTypes.bool,
  footerText: PropTypes.string,
  buttonText: PropTypes.string,
};

PerfiladorUniqueScreen.defaultProps = {
  onSubmit: () => {},
  showFooter: true,
  footerText: '',
  buttonText: '',
};
